import React from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { css } from '@emotion/react';
import { color } from '../../../../styles';
import { IGetDfreeBDeviceAnalyticsResult } from '../../../../types/api/traversal/getDfreeBDeviceAnalytics';
import { useChartResizeObserver } from '../../../../hooks/useChartResizeObserver';
import { CARE_RECORDS } from '../../../../constants';
import { svgIcons } from '.';
import { renderToString } from 'react-dom/server';
import { useTranslation } from 'react-i18next';
import { DEFECATION_ANALYTICS } from '../../../../constants/defecationAnalytics';

interface Props {
  data: IGetDfreeBDeviceAnalyticsResult['countFecesTypes'];
}
const GraphContainerStyle = css`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 24px 16px;
  border-radius: 8px;
  border: 1px solid ${color.border_gray};
  background: ${color.light_gray};
`;

const GraphTitle = css`
  font-size: 24px;
  font-weight: bold;
  margin-bottom: 16px;
`;

const GraphSubtitle = css`
  margin-bottom: 12px;
`;

const mousePointer = (enable: boolean) => css`
  pointer-events: ${enable ? 'auto' : 'none'};
`;

// formatXAxisLabel用のStyle
const XLabelContainer = `
  height: 80px;
  display: flex;
  align-items: center;
  flex-direction: column;
  row-gap: 12px;
`;

// formatXAxisLabel用のStyle
const XLabelImg = `
  flex: 1;
  width: 32px;
`;

// formatXAxisLabel用のStyle
const XLabelText = `
  font-family: 'Noto Sans JP', sans-serif;
  font-weight: normal;
  font-size: 16px;
  line-height: 1.25;
  text-align: center;
`;

export const DefecationHardnessTypeBarChart: React.FC<Props> = ({ data }) => {
  const { t, i18n } = useTranslation();
  const chartRef = React.useRef<HighchartsReact.RefObject>(null);
  useChartResizeObserver(chartRef);
  const hardnessTypeValues = i18n.language.startsWith('ja')
    ? DEFECATION_ANALYTICS.HARDNESS_TYPES_JA
    : DEFECATION_ANALYTICS.HARDNESS_TYPES_EN;
  const categories = CARE_RECORDS.HARDNESS_TYPE_VALUES.map((v) => v.value);
  const series: { y: number; color: string }[] = [
    { y: data[categories[0] as keyof typeof data], color: '#754219' },
    { y: data[categories[1] as keyof typeof data], color: '#754219' },
    { y: data[categories[2] as keyof typeof data], color: '#D19136' },
    { y: data[categories[3] as keyof typeof data], color: '#D19136' },
    { y: data[categories[4] as keyof typeof data], color: '#F3C346' },
    { y: data[categories[5] as keyof typeof data], color: '#F3C346' },
    { y: data[categories[6] as keyof typeof data], color: '#F3C346' },
  ];

  const hasNoData = series.every((item) => item.y === 0);

  const total: number = Object.values(data).reduce((acc, value) => acc + value, 0);

  const formatXAxisLabel = function (this: Highcharts.AxisLabelsFormatterContextObject) {
    const key = this.value as keyof typeof data;
    const count = isNaN(data[key]) ? 0 : data[key];
    const percentage = count === 0 ? 0 : Math.round((count / total) * 100);

    return `
      <div style="${XLabelContainer}">
        <img src="${svgIcons[key]}" style="${XLabelImg}" />
        <span style="${XLabelText}">
          ${count}${t('times', '回')}<br />(${percentage}%)
        </span>
      </div>
    `;
  };

  const options: Highcharts.Options = React.useMemo(() => {
    return {
      chart: {
        type: 'column',
        height: 280,
        width: 480,
        backgroundColor: 'transparent',
      },
      title: {
        text: '',
      },
      tooltip: {
        formatter: function () {
          return `${hardnessTypeValues[this.x as keyof typeof hardnessTypeValues]}: ${this.y} ${t('times', '回')}`;
        },
      },
      xAxis: {
        categories,
        labels: {
          rotation: 0,
          enabled: true,
          useHTML: true,
          formatter: formatXAxisLabel,
        },
        lineWidth: 0,
      },
      yAxis: {
        min: 0,
        max: hasNoData ? 1 : Math.max(...Object.values(data)),
        labels: {
          enabled: false,
        },
        title: {
          text: '',
        },
        gridLineWidth: 0,
      },
      series: [
        {
          type: 'column',
          name: '回数',
          data: series,
          dataLabels: { enabled: false },
        },
      ],
      plotOptions: {
        column: {
          minPointLength: 2,
          borderRadius: 8,
          pointWidth: 15,
        },
      },
      legend: {
        enabled: false,
      },
      credits: {
        enabled: false,
      },
    };
  }, [data]);

  return (
    <div css={GraphContainerStyle}>
      <div css={GraphTitle}>{t('defecationStatus', '排便状態')}</div>
      <div css={GraphSubtitle}>{t('soiledDataNotIncluded', '※付着データは含まれていません')}</div>
      <div css={mousePointer(!hasNoData)}>
        <HighchartsReact highcharts={Highcharts} options={options} ref={chartRef} />
      </div>
    </div>
  );
};
